import {
  forwardRef,
  type HTMLProps,
  type KeyboardEventHandler,
  type MouseEventHandler,
  useCallback,
} from 'react';

import type { ComponentProps } from '~/chords/ComponentProps';
import { getClassNameOverride } from '~/chords/getClassNameOverride';
import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

import { useTableContext } from '../../hooks/useTableContext';

interface TableRowProps
  extends ComponentProps<
    Omit<HTMLProps<HTMLTableRowElement>, 'onKeyDown' | 'tabIndex' | 'onClick'>
  > {
  onClick?: MouseEventHandler & KeyboardEventHandler;
}

const tableRowVariants = variants({
  base: cn('group'),
  variants: {
    isInteractive: {
      true: cn('cursor-pointer'),
    },
    level: {
      1: cn(''),
      2: cn(''),
      3: cn(''),
      4: cn(''),
      5: cn(''),
    },
    variant: {
      default: cn(''),
      striped: cn(''),
    },
  },
  compoundVariants: [
    {
      level: 1,
      variant: 'striped',
      class: ['odd:bg-level-01'],
    },
    {
      level: 2,
      variant: 'striped',
      class: ['odd:bg-level-02'],
    },
    {
      level: 3,
      variant: 'striped',
      class: ['odd:bg-level-03'],
    },
    {
      level: 4,
      variant: 'striped',
      class: ['odd:bg-level-04'],
    },
    {
      level: 5,
      variant: 'striped',
      class: ['odd:bg-level-05'],
    },
  ],
});

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ children, onClick: onInteract, ...props }, ref) => {
    const { level, variant } = useTableContext();

    const onKeyDown = useCallback<KeyboardEventHandler>(
      (e) => {
        if (e.key === 'Enter') {
          onInteract?.(e);
        }
      },
      [onInteract],
    );

    const onClick = useCallback<MouseEventHandler>(
      (e) => {
        onInteract?.(e);
      },
      [onInteract],
    );

    const className = getClassNameOverride(props, 'TableRow');

    return (
      <tr
        ref={ref}
        className={cn(
          tableRowVariants({ isInteractive: !!onInteract, level, variant }),
          className,
        )}
        tabIndex={onInteract ? 0 : undefined}
        onClick={onClick}
        onKeyDown={onKeyDown}
        {...props}
      >
        {children}
      </tr>
    );
  },
);

TableRow.displayName = 'TableRow';
